import PropTypes from "prop-types";
import {
  createContext,
  useEffect,
  useReducer,
  useCallback,
  useMemo,
  useState,
} from "react";
// utils
import axios, { VENUE_STAFF_AUTH_URL, isStaffSession, getRefreshTokensUrl } from "../utils/axios";
import { gatherBrowserInfo } from "../utils/gatherBrowserInfo";
import { PATH_DASHBOARD } from "../routes/paths";
import localStorageAvailable from "../utils/localStorageAvailable";
import { useNavigate } from "react-router-dom";
//
import { isValidToken, setSession, setRefresh } from "./utils";
import { setCurrencyContext, setNumberFormatContext } from "../utils/formatNumber";
import { setDateTimeFormats } from "../utils/formatTime";
const backendUrl = process.env.REACT_APP_BACKEND_URL;

const applyFranchiseCurrency = (user) => {
  setCurrencyContext(user?.venue?.franchiseId?.currency);
  setNumberFormatContext(user?.venue?.franchiseId);
  return user;
};

const VENUE_SELECTION_KEY = "venueSelection";

const getId = (value) => value?.id || value?._id || value;

const getStoredVenueSelection = () => {
  try {
    return JSON.parse(sessionStorage.getItem(VENUE_SELECTION_KEY));
  } catch (error) {
    return null;
  }
};

const setStoredVenueSelection = (venueSelection) => {
  if (venueSelection) {
    sessionStorage.setItem(VENUE_SELECTION_KEY, JSON.stringify(venueSelection));
  } else {
    sessionStorage.removeItem(VENUE_SELECTION_KEY);
  }
};

// The V2 staff profile carries the venue and franchise documents separately, so rebuild the V1 session
// shape (user.venue.franchiseId, venueId) the rest of the app reads. No extra API calls are made here.
const buildVenueSession = async (profile) => {
  const venueId = getId(profile?.venueId);
  // The profile sends `venue`/`franchise` documents with `venueId`/`franchiseId` as plain ids; older responses
  // populated the id fields instead. The app expects user.venue with a populated franchiseId.
  const venueDoc = profile?.venue || (typeof profile?.venueId === "object" ? profile.venueId : {});
  const franchise = profile?.franchise || (typeof profile?.franchiseId === "object" ? profile.franchiseId : null);
  const venue = { ...venueDoc, franchiseId: franchise || venueDoc?.franchiseId };

  const user = {
    ...profile,
    venue,
    venueId,
    displayName: `${profile?.firstName} ${profile?.lastName}`,
  };

  return {
    user,
    // Not part of the V2 auth response; empty lists keep the context helpers (add/update/delete) safe.
    paymentMethods: { results: [] },
    bankDiscounts: { results: [] },
    promotionV2Enabled: Boolean(venue?.franchiseId),
  };
};

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
  paymentMethods: null,
  bankDiscounts: null,
  promotionV2Enabled: true,
};

const reducer = (state, action) => {
  if (action.type === "INITIAL") {
    return {
      isInitialized: true,
      isAuthenticated: action?.payload?.isAuthenticated,
      user: action?.payload?.user,
      paymentMethods: action?.payload?.paymentMethods?.results,
      bankDiscounts: action?.payload?.bankDiscounts?.results,
      promotionV2Enabled: action?.payload?.promotionV2Enabled !== false,
    };
  }
  if (action.type === "LOGIN") {
    return {
      ...state,
      isAuthenticated: true,
      user: action?.payload?.user,
      paymentMethods: action?.payload?.paymentMethods?.results,
      bankDiscounts: action?.payload?.bankDiscounts?.results,
      promotionV2Enabled: action?.payload?.promotionV2Enabled !== false,
    };
  }
  if (action.type === "REGISTER") {
    return {
      ...state,
      isAuthenticated: true,
      user: action?.payload?.user,
      paymentMethods: action?.payload?.paymentMethods?.results,
      bankDiscounts: action?.payload?.bankDiscounts?.results
    };
  }


  if (action.type === "RESET") {
    return {
      ...state,
      isAuthenticated: true,
      user: action?.payload?.user,
    };
  }

  if (action.type === "LOGOUT") {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
      paymentMethods: null,
      bankDiscounts: null,
      promotionV2Enabled: false,
    };
  }
  if (action.type === "UPDATE_USER") {
    return {
      ...state,
      user: action?.payload?.user,
    };
  }
  if (action.type === "ADD_PAYMENT_METHOD") {
    const newPaymentMethod = action.payload?.newPaymentMethod;
    const newId = newPaymentMethod?.id || newPaymentMethod?._id;
    if (!newId) { return state }
    return {
      ...state,
      paymentMethods: [
        ...(state.paymentMethods || []),
        {
          ...newPaymentMethod,
          id: newId,
        },
      ],
    };
  }

  if (action.type === "UPDATE_PAYMENT_METHOD") {
    const updatedPaymentMethod = action.payload?.updatedPaymentMethod;
    const updatedId = updatedPaymentMethod?.id || updatedPaymentMethod?._id;
    if (!updatedId) { return state }
    return {
      ...state,
      paymentMethods: (state.paymentMethods || []).map((method) => {
        const methodId = method?.id || method?._id;
        return methodId === updatedId
          ? {
            ...updatedPaymentMethod,
            id: updatedId,
          }
          : method;
      }),
    };
  }

   if (action.type === "UPDATE_BANKDISCOUNT") {
    return {
      ...state,
      bankDiscounts: action?.payload?.bankDiscounts,
    };
  }
  if (action.type === "UPDATE_VENUE") {
    return {
      ...state,
      user: {
        ...state.user,
        venue: action.payload.venue,
      },
    };
  }
  if (action.type === "UPDATE_VENUE_DATA") {    
    return {
      ...state,
      user: {
        ...state.user,
        venue: {
          ...state.user.venue,
          ...action.payload,
          configurations: {
            ...action.payload.configurations,
          },
        },
      },
    };
  }
  if (action.type === "DELETE_PAYMENT_METHOD") {
    return {
      ...state,
      paymentMethods: action?.payload?.paymentMethods,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  // { venues, selectionToken, browserInfo, closable } while the venue selection modal is open
  const [venueSelection, setVenueSelection] = useState(null);
  const navigate = useNavigate();
  const storageAvailable = localStorageAvailable();

  useEffect(() => {
    setDateTimeFormats(state.user?.venue?.franchiseId);
    setNumberFormatContext(state.user?.venue?.franchiseId);
  }, [state.user?.venue?.franchiseId]);

  const initialize = useCallback(async () => {
    try {
      if (window.location.pathname === "/impersonate") {
        dispatch({
          type: "INITIAL",
          payload: { isAuthenticated: false, user: null, paymentMethods: null, bankDiscounts: null },
        });
        return;
      }

      const refreshToken = storageAvailable
        ? sessionStorage.getItem("refreshToken")
        : "";
      let accessToken = storageAvailable
        ? sessionStorage.getItem("accessToken")
        : "";

      if (refreshToken) {
        let activeRefreshToken = refreshToken;
        if (!isValidToken(accessToken)) {
          const refreshResponse = await axios.post(getRefreshTokensUrl(refreshToken), {
            refreshToken,
            browserInfo: { userAgent: window.navigator.userAgent },
          });
          const refreshedTokens = refreshResponse.data?.tokens || refreshResponse.data;
          accessToken = refreshedTokens?.access?.token;
          activeRefreshToken = refreshedTokens?.refresh?.token;
          if (!accessToken || !activeRefreshToken) throw new Error("Unable to refresh session");
        }

        setSession(accessToken);
        setRefresh(activeRefreshToken);

        let sessionData;
        if (isStaffSession(activeRefreshToken)) {
          const response = await axios.post(`${VENUE_STAFF_AUTH_URL}/get-profile`, {
            refreshToken: activeRefreshToken,
          });
          sessionData = await buildVenueSession(response.data?.user);
        } else {
          // const response = await axios.get('/api/account/my-account');
          const response = await axios.post(`${backendUrl}/authv/get-profile`, {
            refreshToken: activeRefreshToken,
          });
          sessionData = response.data;
        }

        const { user, promotionV2Enabled } = sessionData;
        const { paymentMethods , bankDiscounts} = sessionData;
        applyFranchiseCurrency(user);

        user.displayName = `${user?.firstName} ${user?.lastName} `;
        if (user?.venue?.status === "inActive") {
          dispatch({
            type: "INITIAL",
            payload: {
              isAuthenticated: false,
              user: null,
              paymentMethods: null,
              bankDiscounts: null,
              promotionV2Enabled: false,
            },
          });
        } else {
          dispatch({
            type: "INITIAL",
            payload: {
              isAuthenticated: true,
              user,
              paymentMethods,
              bankDiscounts,
              promotionV2Enabled,
            },
          });
        }
      } else {
        dispatch({
          type: "INITIAL",
          payload: {
            isAuthenticated: false,
            user: null,
            paymentMethods: null,
            bankDiscounts: null,
            promotionV2Enabled: false,
          },
        });
      }
    } catch (error) {
      console.error(error);
      setSession(null);
      setRefresh(null);
      dispatch({
        type: "INITIAL",
        payload: {
          isAuthenticated: false,
          user: null,
          paymentMethods: null,
          bankDiscounts: null,
          promotionV2Enabled: false,
        },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const navigateAfterLogin = useCallback((user) => {
    if (user.permissions?.canViewDashboard) {
      navigate("/dashboard/app");
    } else if (user.permissions?.canViewPos) {
      navigate("/dashboard/pos");
    } else if (user.permissions?.canSeeReservationCalendar) {
      navigate("/dashboard/reservation/calendar");
    } else if (user.permissions?.canEditOrderOnPOS) {
      navigate("/dashboard/tables");
    } else if (user?.permissions?.canSeeOrderProcessingScreen) {
      navigate("/dashboard/orders");
    } else if (user?.permissions?.canSeeKitchenScreen) {
      navigate("/dashboard/kitchen");
    } else if (user?.permissions?.canViewStaff) {
      navigate(PATH_DASHBOARD.hrm.staff);
    } else if (user?.venue?.franchiseId?.configurations?.isWarehouseAvailable) {
      navigate("/dashboard/warehouse/purchaseOrder/display");
    } else {
      navigate("/dashboard/menu/display");
    }
  }, [navigate]);

  // Completes authentication with the tokens returned by login (single venue) or select-venue.
  const completeVenueLogin = useCallback(async ({ tokens, user: profile }) => {
    const { user, paymentMethods, bankDiscounts, promotionV2Enabled } = await buildVenueSession(profile);

    setSession(tokens?.access?.token);
    setRefresh(tokens?.refresh?.token);
    applyFranchiseCurrency(user);
    dispatch({
      type: "LOGIN",
      payload: {
        user,
        paymentMethods,
        bankDiscounts,
        promotionV2Enabled,
      },
    });
    navigateAfterLogin(user);
    return user;
  }, [navigateAfterLogin]);

  // LOGIN
  const login = useCallback(async (userName, password, ipAddress, browserInfo) => {
    const response = await axios.post(`${VENUE_STAFF_AUTH_URL}/login`, {
      userName,
      password,
      ipAddress,
      browserInfo
    });

    if (response.data?.requiresVenueSelection === true) {
      const { selectionToken, venues } = response.data;
      const selection = { selectionToken, venues, browserInfo };
      setStoredVenueSelection(selection);
      setVenueSelection({ ...selection, closable: false });
      return { requiresVenueSelection: true };
    }

    setStoredVenueSelection(null);
    const user = await completeVenueLogin(response.data);
    return { requiresVenueSelection: false, user };
  }, [completeVenueLogin]);

  // SELECT VENUE (after login, or when switching venue from an authenticated session)
  const selectVenue = useCallback(async (venueId) => {
    const selection = venueSelection || getStoredVenueSelection();
    const browserInfo = selection?.browserInfo || (await gatherBrowserInfo());
    const response = await axios.post(`${VENUE_STAFF_AUTH_URL}/select-venue`, {
      selectionToken: selection?.selectionToken?.token,
      venueId,
      browserInfo,
    });
    const user = await completeVenueLogin(response.data);
    setVenueSelection(null);
    return user;
  }, [venueSelection, completeVenueLogin]);

  // SWITCH VENUE (authenticated user; the backend revokes the previous session)
  const switchVenue = useCallback(async (venueId) => {
    const browserInfo = venueSelection?.browserInfo || (await gatherBrowserInfo());
    const response = await axios.post(`${VENUE_STAFF_AUTH_URL}/switch-venue`, {
      refreshToken: sessionStorage.getItem("refreshToken"),
      venueId,
      browserInfo,
    });
    const user = await completeVenueLogin(response.data);
    setVenueSelection(null);
    return user;
  }, [venueSelection, completeVenueLogin]);

  const openVenueSwitcher = useCallback(() => {
    const selection = getStoredVenueSelection();
    if (!selection?.venues?.length) return false;
    setVenueSelection({ ...selection, closable: true });
    return true;
  }, []);

  // During login the modal is not closable; `force` is only used to abandon a failed login selection.
  const closeVenueSelection = useCallback((force = false) => {
    if (!venueSelection || (!venueSelection.closable && !force)) return;
    if (!venueSelection.closable) setStoredVenueSelection(null);
    setVenueSelection(null);
  }, [venueSelection]);

  const exchangeImpersonation = useCallback(async (code, browserInfo) => {
    const response = await axios.post(`${backendUrl}/authv/impersonation/exchange`, {
      code,
      browserInfo,
    });
    const { tokens, user, scope, paymentMethods, bankDiscounts, promotionV2Enabled } = response.data;
    if (
      scope !== "venue"
      || user?.auth !== "venue"
      || !user?.isImpersonating
      || !tokens?.access?.token
      || !tokens?.refresh?.token
    ) {
      throw new Error("Invalid venue impersonation response");
    }

    setSession(tokens.access.token);
    setRefresh(tokens.refresh.token);
    applyFranchiseCurrency(user);
    user.displayName = user.displayName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
    dispatch({
      type: "LOGIN",
      payload: { user, paymentMethods, bankDiscounts, promotionV2Enabled },
    });
    navigate("/dashboard/app", { replace: true });
    return user;
  }, [navigate]);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    const response = await axios.post(
      "https://api-dev-minimal-v4.vercel.app/api/account/register",
      {
        email,
        password,
        firstName,
        lastName,
      }
    );
    const { accessToken, user } = response.data;

    sessionStorage.setItem("accessToken", accessToken);

    dispatch({
      type: "REGISTER",
      payload: {
        user,
      },
    });
  }, []);

  // LOGOUT
  const logout = useCallback(() => {
    setSession(null);
    setRefresh(null);
    dispatch({
      type: "LOGOUT",
    });
  }, []);

  // Reset
  const resetPassword = useCallback(
    async (old_password, new_password, user) => {
      const response = await axios.patch(
        `${backendUrl}/venueUsers/changePassword/${user?.id}`,
        {
          old_password,
          new_password,
        }
      );

      dispatch({
        type: "RESET",
        payload: {
          user,
        },
      });
      return response;
    },
    []
  );

  const updateUser = useCallback((updatedUser) => {
    updatedUser.displayName = `${updatedUser?.firstName} ${updatedUser?.lastName}`;
    dispatch({
      type: "UPDATE_USER",
      payload: {
        user: updatedUser,
      },
    });
  }, []);

  const addPaymentMethod = useCallback((newPaymentMethod) => {
    const newId = newPaymentMethod?.id || newPaymentMethod?._id;
    if (!newId) return;
    dispatch({
      type: "ADD_PAYMENT_METHOD",
      payload: {
        newPaymentMethod: {
          ...newPaymentMethod,
          id: newId,
        },
      },
    });
  }, []);

  const updatePaymentMethod = useCallback((updatedPaymentMethod) => {
    const updatedId = updatedPaymentMethod?.id || updatedPaymentMethod?._id;
    if (!updatedId) return;
    dispatch({
      type: "UPDATE_PAYMENT_METHOD",
      payload: {
        updatedPaymentMethod: {
          ...updatedPaymentMethod,
          id: updatedId,
        },
      },
    });
  }, []);
  const deletePaymentMethod = useCallback(
    (paymentMethodId) => {
      const updatedPaymentMethods = state.paymentMethods.filter(
        (method) => method.id !== paymentMethodId
      );
      dispatch({
        type: "UPDATE_PAYMENT_METHOD",
        payload: {
          paymentMethods: updatedPaymentMethods,
        },
      });
    },
    [state.paymentMethods]
  );

  const updateVenue = useCallback(
    (updateVenue) => {
      dispatch({
        type: "UPDATE_VENUE",
        payload: {
          venue: updateVenue
        },
      });
    },
    []
  );

  const updateVenueData = useCallback((updates) => {
    dispatch({
      type: "UPDATE_VENUE_DATA",
      payload: updates,
    });
  }, []);

const addBankDiscount = useCallback(
  (newBankDiscount) => {
    const updatedBankDiscounts = [...state.bankDiscounts, newBankDiscount];
    dispatch({
      type: "UPDATE_BANKDISCOUNT",
      payload: {
        bankDiscounts: updatedBankDiscounts,
      },
    });
  },
  [state.bankDiscounts]
);


const updateBankDiscount = useCallback(
  (updatedBankDiscount) => {
    dispatch({
      type: "UPDATE_BANKDISCOUNT",
      payload: {
        bankDiscounts: state.bankDiscounts.map((discount) =>
          discount.id === updatedBankDiscount.id ? updatedBankDiscount : discount
        ),
      },
    });
  },
  [state.bankDiscounts]
);

const deleteBankDiscount = useCallback(
  (bankDiscountId) => {
    const updatedBankDiscounts = state.bankDiscounts.filter(
      (discount) => discount.id !== bankDiscountId
    );
    dispatch({
      type: "UPDATE_BANKDISCOUNT",
      payload: {
        bankDiscounts: updatedBankDiscounts,
      },
    });
  },
  [state.bankDiscounts]
);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      availablePaymentOptions: state.paymentMethods,
      bankDiscounts: state.bankDiscounts,
      promotionV2Enabled: state.promotionV2Enabled,
      method: "jwt",
      venueSelection,
      canSwitchVenue: (getStoredVenueSelection()?.venues?.length || 0) > 1,
      login,
      selectVenue,
      switchVenue,
      openVenueSwitcher,
      closeVenueSelection,
      exchangeImpersonation,
      register,
      logout,
      resetPassword,
      updateUser,
      addPaymentMethod,
      updatePaymentMethod,
      deletePaymentMethod,
      addBankDiscount,
      updateBankDiscount,
      deleteBankDiscount,
      updateVenue,
      updateVenueData
    }),
    [
      state.isAuthenticated,
      state.isInitialized,
      state.user,
      state.paymentMethods,
      state.bankDiscounts,
      state.promotionV2Enabled,
      venueSelection,
      login,
      selectVenue,
      switchVenue,
      openVenueSwitcher,
      closeVenueSelection,
      exchangeImpersonation,
      logout,
      register,
      resetPassword,
      updateUser,
      addPaymentMethod,
      updatePaymentMethod,
      deletePaymentMethod,
      addBankDiscount,
      updateBankDiscount,
      deleteBankDiscount,
      updateVenue,
      updateVenueData
    ]
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
}
